import { MessageSquare } from 'lucide-react';
import {
    type ReactNode,
    useCallback,
    useEffect,
    useRef,
    useState,
} from 'react';

import { type ActivityAssigneeOption } from '@apps/web/components/home/activity_assignment';
import { ConversationBoard } from '@apps/web/components/shared/messages/conversation_board';
import {
    type ConversationInboxTab,
    type ConversationInboxView,
    ConversationListPanelHeader,
    type ConversationOfficeFilter,
    type ConversationQueueView,
    type ConversationReadFilter,
} from '@apps/web/components/shared/messages/conversation_list_panel_header';
import { ConversationListRow } from '@apps/web/components/shared/messages/conversation_list_row';
import { NewConversationMessageDialog } from '@apps/web/components/shared/messages/new_conversation_message_dialog';
import { Button } from '@apps/web/components/ui/button';
import { Skeleton } from '@apps/web/components/ui/skeleton';
import { useOrganizationTimezone } from '@apps/web/context/timezone_provider';
import { useOrganizationConversationTyping } from '@apps/web/hooks/use_conversation_typing';
import { type BlockedCaregiverInfo } from '@apps/web/swr_data_fetching/data_fetching_hooks';
import { type OrganizationConversationMetadata } from '@libraries/typescript/api_client';
import {
    type OfficeConversationCount,
    type OrganizationOffice,
} from '@libraries/typescript/api_client/schema_types';

type ConversationListPanelProps = {
    readonly conversations: Array<OrganizationConversationMetadata>;
    readonly pinnedConversation?: OrganizationConversationMetadata | null;
    readonly pinnedConversationLabel?: string;
    readonly isLoading: boolean;
    readonly isLoadingMore: boolean;
    readonly hasMore: boolean;
    readonly onLoadMore: () => void;
    readonly search: string;
    readonly setSearch: (search: string) => void;
    readonly pendingFirst: boolean;
    readonly setPendingFirst: (enabled: boolean) => void;
    readonly activeTab?: ConversationInboxTab;
    readonly setActiveTab?: (tab: ConversationInboxTab) => void;
    readonly totalUnreadCount: number;
    readonly totalPendingCount: number;
    readonly selectedConversationId?: string | null;
    readonly onSelectConversation: (conversationId: string) => void;
    readonly onMarkAllAsRead?: () => Promise<void>;
    readonly isMarkingAllAsRead?: boolean;
    readonly getBlockedInfosForCaregiver?: (
        caregiverId: string | null | undefined,
    ) => Array<BlockedCaregiverInfo>;
    readonly onUnblocked?: () => void;
    readonly markAsRead: (conversationId: string) => Promise<void>;
    readonly markAsUnread: (conversationId: string) => Promise<void>;
    readonly title?: string | null;
    readonly organizationId?: string | null;
    readonly onConversationMessageCreated?: (
        conversationId: string,
    ) => void | Promise<void>;
    readonly officeOptions?: ReadonlyArray<OrganizationOffice>;
    readonly officeFilter?: ConversationOfficeFilter;
    readonly onOfficeFilterChange?: (filter: ConversationOfficeFilter) => void;
    readonly officeConversationCounts?: ReadonlyArray<OfficeConversationCount>;
    readonly unassignedOfficeCount?: number;
    // Conversation ownership (flag `conversation_ownership_enabled`).
    readonly ownershipEnabled?: boolean;
    readonly queueView?: ConversationQueueView;
    readonly setQueueView?: (view: ConversationQueueView) => void;
    readonly inboxView?: ConversationInboxView;
    readonly setInboxView?: (view: ConversationInboxView) => void;
    readonly readFilter?: ConversationReadFilter | null;
    readonly setReadFilter?: (filter: ConversationReadFilter | null) => void;
    readonly careCoordinatorFilter?: ReactNode;
    readonly currentUserId?: string | null;
    readonly boardAssignees?: ReadonlyArray<ActivityAssigneeOption>;
    readonly onReassignConversation?: (
        conversationId: string,
        assignedToUserId: string | null,
    ) => void;
    readonly savedViews?: ReactNode;
    // Recency window: when set, the list only reaches this many days back
    // and the footer offers to reveal the full history. When the window is
    // deliberately off, `onShowRecentConversations` renders the way back.
    readonly recentWindowDays?: number | null;
    readonly onShowOlderConversations?: () => void;
    readonly onShowRecentConversations?: () => void;
};

export function OrganizationConversationListPanel({
    conversations,
    pinnedConversation = null,
    pinnedConversationLabel = 'Pinned conversation',
    isLoading,
    isLoadingMore,
    hasMore,
    onLoadMore,
    search,
    setSearch,
    pendingFirst,
    setPendingFirst,
    activeTab = 'all',
    setActiveTab,
    totalUnreadCount,
    totalPendingCount,
    selectedConversationId = null,
    onSelectConversation,
    onMarkAllAsRead,
    isMarkingAllAsRead = false,
    markAsRead,
    markAsUnread,
    title = 'Conversations',
    organizationId = null,
    onConversationMessageCreated,
    officeOptions,
    officeFilter,
    onOfficeFilterChange,
    officeConversationCounts,
    unassignedOfficeCount,
    ownershipEnabled = false,
    queueView,
    setQueueView,
    inboxView = 'list',
    setInboxView,
    readFilter,
    setReadFilter,
    careCoordinatorFilter,
    currentUserId = null,
    boardAssignees = [],
    onReassignConversation,
    savedViews,
    recentWindowDays = null,
    onShowOlderConversations,
    onShowRecentConversations,
}: ConversationListPanelProps) {
    const { timezone } = useOrganizationTimezone();
    // One panel-level subscription feeds live "… is typing…" presence to every
    // row, so the non-virtualized list never mounts a per-row org-event listener.
    const typingByConversation = useOrganizationConversationTyping({
        organizationId,
        currentUserId,
        enabled: !!organizationId,
    });
    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const loadMoreRef = useRef<HTMLDivElement>(null);
    const [isNewMessageDialogOpen, setIsNewMessageDialogOpen] = useState(false);
    // Rendered both at the end of a windowed list and in its empty state, so
    // a coordinator can always reach history older than the window — and,
    // symmetrically, get back to the recent view after revealing it.
    const showOlderConversationsFooter =
        recentWindowDays !== null && onShowOlderConversations ? (
            <div className="px-2 py-3 text-center">
                <p className="text-muted-foreground text-xs">
                    Showing the last {recentWindowDays} days
                </p>
                <Button
                    className="mt-1"
                    onClick={onShowOlderConversations}
                    size="sm"
                    type="button"
                    variant="ghost"
                >
                    Show older conversations
                </Button>
            </div>
        ) : recentWindowDays === null && onShowRecentConversations ? (
            <div className="px-2 py-3 text-center">
                <p className="text-muted-foreground text-xs">
                    Showing all conversations
                </p>
                <Button
                    className="mt-1"
                    onClick={onShowRecentConversations}
                    size="sm"
                    type="button"
                    variant="ghost"
                >
                    Show recent only
                </Button>
            </div>
        ) : null;
    const uniqueConversations = conversations.filter(
        (conversation, index, allConversations) =>
            allConversations.findIndex(
                (candidate) => candidate.id === conversation.id,
            ) === index,
    );
    const regularConversations =
        pinnedConversation === null
            ? uniqueConversations
            : uniqueConversations.filter((conversation) => {
                  return conversation.id !== pinnedConversation.id;
              });
    const hasVisibleConversations =
        pinnedConversation !== null || regularConversations.length > 0;

    const loadMoreIfPossible = useCallback(() => {
        if (!hasMore || isLoadingMore) {
            return;
        }

        onLoadMore();
    }, [hasMore, isLoadingMore, onLoadMore]);

    useEffect(() => {
        const sentinel = loadMoreRef.current;
        const scrollContainer = scrollContainerRef.current;

        if (!sentinel || !scrollContainer || !hasMore) {
            return;
        }

        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0]?.isIntersecting) {
                    loadMoreIfPossible();
                }
            },
            {
                root: scrollContainer,
                rootMargin: '0px 0px 200px 0px',
            },
        );

        observer.observe(sentinel);

        return () => {
            observer.disconnect();
        };
    }, [hasMore, loadMoreIfPossible]);

    function renderConversationListRow(
        conversation: OrganizationConversationMetadata,
    ) {
        return (
            <ConversationListRow
                conversation={conversation}
                key={conversation.id}
                markAsRead={markAsRead}
                markAsUnread={markAsUnread}
                onSelect={() => {
                    onSelectConversation(conversation.id);
                }}
                selected={conversation.id === selectedConversationId}
                showOwnership={ownershipEnabled}
                typingUsers={typingByConversation[conversation.id]}
            />
        );
    }

    const showBoard =
        ownershipEnabled && inboxView === 'board' && !!onReassignConversation;

    return (
        <div className="bg-background flex h-full min-h-0 flex-col overflow-hidden">
            <ConversationListPanelHeader
                isMarkingAllAsRead={isMarkingAllAsRead}
                onMarkAllAsRead={onMarkAllAsRead}
                onNewMessage={
                    organizationId && onConversationMessageCreated
                        ? () => {
                              setIsNewMessageDialogOpen(true);
                          }
                        : undefined
                }
                activeTab={activeTab}
                setActiveTab={setActiveTab}
                pendingFirst={pendingFirst}
                search={search}
                setPendingFirst={setPendingFirst}
                setSearch={setSearch}
                title={title}
                totalPendingCount={totalPendingCount}
                totalUnreadCount={totalUnreadCount}
                officeOptions={officeOptions}
                officeFilter={officeFilter}
                onOfficeFilterChange={onOfficeFilterChange}
                officeConversationCounts={officeConversationCounts}
                unassignedOfficeCount={unassignedOfficeCount}
                ownershipEnabled={ownershipEnabled}
                queueView={queueView}
                setQueueView={setQueueView}
                inboxView={inboxView}
                setInboxView={setInboxView}
                readFilter={readFilter}
                setReadFilter={setReadFilter}
                careCoordinatorFilter={careCoordinatorFilter}
                savedViews={savedViews}
            />
            {showBoard ? (
                <ConversationBoard
                    organizationId={organizationId ?? ''}
                    currentUserId={currentUserId}
                    timezone={timezone}
                    conversations={conversations}
                    search={search}
                    assignees={boardAssignees}
                    hasMore={hasMore}
                    isLoadingMore={isLoadingMore}
                    onLoadMore={onLoadMore}
                    selectedConversationId={selectedConversationId}
                    onSelectConversation={onSelectConversation}
                    onReassign={
                        onReassignConversation ??
                        (() => {
                            // No-op when the board is shown without a reassign
                            // handler; `showBoard` already guards this.
                        })
                    }
                />
            ) : (
                <div
                    className="flex-1 overflow-y-auto"
                    ref={scrollContainerRef}
                >
                    {isLoading ? (
                        <div className="space-y-1 px-2 py-1">
                            {Array.from({ length: 6 }).map((_, index) => (
                                <div
                                    className="bg-muted flex items-center gap-2.5 rounded-lg px-2.5 py-2"
                                    key={index}
                                >
                                    <Skeleton className="size-8 rounded-full" />
                                    <div className="min-w-0 flex-1 space-y-1">
                                        <Skeleton className="h-3.5 w-32" />
                                        <Skeleton className="h-3 w-full" />
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : !hasVisibleConversations ? (
                        <div className="p-8 text-center">
                            <MessageSquare className="text-muted-foreground/50 mx-auto mb-3 h-12 w-12" />
                            <p className="text-muted-foreground">
                                {recentWindowDays !== null
                                    ? 'No recent conversations'
                                    : 'No conversations found'}
                            </p>
                            {showOlderConversationsFooter}
                        </div>
                    ) : (
                        <div className="space-y-1 px-2 py-1">
                            {pinnedConversation ? (
                                <section aria-label={pinnedConversationLabel}>
                                    <div className="text-muted-foreground px-2 pb-2 text-[11px] font-semibold tracking-[0.14em] uppercase">
                                        {pinnedConversationLabel}
                                    </div>
                                    {renderConversationListRow(
                                        pinnedConversation,
                                    )}
                                </section>
                            ) : null}
                            {regularConversations.length > 0 ? (
                                <section
                                    aria-label={
                                        pinnedConversation
                                            ? 'All conversations'
                                            : (title ?? 'All conversations')
                                    }
                                >
                                    {pinnedConversation ? (
                                        <div className="text-muted-foreground px-2 pb-2 text-[11px] font-semibold tracking-[0.14em] uppercase">
                                            All conversations
                                        </div>
                                    ) : null}
                                    <div className="space-y-1">
                                        {regularConversations.map(
                                            renderConversationListRow,
                                        )}
                                    </div>
                                </section>
                            ) : null}
                            {hasMore ? (
                                <div
                                    aria-hidden="true"
                                    className="h-1"
                                    ref={loadMoreRef}
                                />
                            ) : (
                                showOlderConversationsFooter
                            )}
                        </div>
                    )}
                    {isLoadingMore && hasMore ? (
                        <div className="text-muted-foreground p-4 text-center">
                            Loading more…
                        </div>
                    ) : null}
                </div>
            )}
            {organizationId && onConversationMessageCreated ? (
                <NewConversationMessageDialog
                    onMessageCreated={onConversationMessageCreated}
                    onOpenChange={setIsNewMessageDialogOpen}
                    open={isNewMessageDialogOpen}
                    organizationId={organizationId}
                />
            ) : null}
        </div>
    );
}
